import React from 'react';
import { Box, Flex, Text, UnstyledButton } from '@mantine/core';
import { IconMapPin } from '@tabler/icons-react';
import clsx from 'clsx';

import type { Competitor } from '../../codegen/graphql';
import {
    getCompetitorLogoSrc,
    getCompanyInitials,
} from '../../utility/competitorDisplay';

export interface CompetitorListRowProps {
    competitor: Competitor;
    disabled: boolean;
    onPick: (competitor: Competitor) => void;
}

export function CompetitorListRow({
    competitor,
    disabled,
    onPick,
}: CompetitorListRowProps) {
    const logoSrc = getCompetitorLogoSrc(competitor.name);
    const addressLine = competitor.address.split('\n')[0] ?? '';

    return (
        <UnstyledButton
            type="button"
            disabled={disabled}
            onClick={() => onPick(competitor)}
            className={clsx(
                'flex w-full flex-row items-center px-3 py-1.5 text-left',
                disabled && 'cursor-not-allowed opacity-50',
            )}
        >
            {logoSrc ? (
                <Box
                    component="img"
                    src={logoSrc}
                    alt=""
                    className="h-12 w-12 shrink-0 rounded-full object-cover"
                />
            ) : (
                <Flex
                    align="center"
                    justify="center"
                    className="h-12 w-12 shrink-0 rounded-full bg-[#A7C7E7]"
                >
                    <Text className="text-lg font-bold leading-none text-[#174065]">
                        {getCompanyInitials(competitor.name)}
                    </Text>
                </Flex>
            )}
            <Box className="ml-2 min-w-0 flex-1">
                <Text className="text-base font-medium leading-tight text-[#222]">
                    {competitor.name}
                </Text>
                {addressLine ? (
                    <Flex
                        gap={6}
                        align="center"
                        wrap="nowrap"
                        className="mt-0.5 min-w-0"
                    >
                        <IconMapPin
                            size={18}
                            className="shrink-0 text-[#848484]"
                            aria-hidden
                        />
                        <Text
                            size="sm"
                            className="min-w-0 truncate text-[#848484]"
                        >
                            {addressLine}
                        </Text>
                    </Flex>
                ) : null}
                {disabled ? (
                    <Text size="xs" c="dimmed" className="mt-0.5">
                        Already on this survey
                    </Text>
                ) : null}
            </Box>
        </UnstyledButton>
    );
}
